'use client'

import {FC} from 'react'

import {Button} from '@/components/common/content/Button'
import {ButtonVariant} from '@/components/common/content/ButtonTypes'
import {Section} from '@/components/common/content/Section'
import {Text, TextVariant} from '@/components/common/content/Text'
import {Page} from '@/components/Page'
import {useErrorContent} from '@/content/error'
import {Link, useTranslation} from '@/i18n'
import {theme} from '@/theme/theme'
import {MOBILE_SCREENSIZE} from '@/utils/styles/breakpoints'

// status codes that have custom error pages and translation content
const supportedErrorCodes = [404, 410, 500]

type ErrorPageLayoutProps = {
  statusCode: number
}

export const ErrorPageLayout: FC<React.PropsWithChildren<ErrorPageLayoutProps>> = ({
  statusCode,
  children,
}) => {
  const {t} = useTranslation()
  const errorCode = supportedErrorCodes.includes(statusCode) ? statusCode : 500
  const {title, linkText, linkHref} = useErrorContent(errorCode)

  return (
    <>
      <Page>
        <Section hasPaddingTop hasPaddingBottom>
          <div className="wrapper">
            <h1 className="number">{statusCode}</h1>
            <Text variant={TextVariant.h2} align="center">
              {t(title)}
            </Text>
            <div className="button-wrapper">
              <Link href={linkHref}>
                <Button variant={ButtonVariant.primary} type="button">
                  {t(linkText)}
                </Button>
              </Link>
            </div>
          </div>
        </Section>
        {children}
      </Page>

      <style jsx>{`
        .wrapper {
          padding-top: ${theme.spacing[8]};
          padding-bottom: ${theme.spacing[8]};
        }

        .number {
          font-size: 220px;
          line-height: 170px;
          text-align: center;
          margin: 0 0 ${theme.spacing[5]};
          color: ${theme.palette.secondary.main};
        }

        .button-wrapper {
          margin-top: ${theme.spacing[5]};
          text-align: center;
        }

        @media ${MOBILE_SCREENSIZE} {
          .wrapper {
            padding-top: ${theme.spacing[5]};
            padding-bottom: ${theme.spacing[5]};
          }

          .number {
            font-size: 120px;
            line-height: 90px;
            margin: 0 0 ${theme.spacing[4]};
          }

          .button-wrapper {
            margin-top: ${theme.spacing[4]};
          }
        }
      `}</style>
    </>
  )
}
